import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AppModal } from '../../../../components/ui/AppModal';
import type { RightsProfile } from '../../types/rights-profile.types';
import type { RemoveOutcome } from '../../hooks/useRightsProfiles';

interface DeleteProfileDialogProps {
  /** The profile to delete; `null` closes the dialog. */
  profile: RightsProfile | null;
  onClose: () => void;
  onDeleted: (profile: RightsProfile) => void;
  /** Performs the delete and reports the real HTTP outcome (from `useRightsProfiles.remove`). */
  onConfirm: (profile: RightsProfile) => Promise<RemoveOutcome>;
}

interface BlockedState {
  profileName: string;
  assignedUsersCount: number;
}

/**
 * Two-state delete dialog:
 * - confirm view — "permanently delete «X»?" (always the initial state);
 * - blocked view — "deletion not possible, assigned to N user(s)".
 *
 * The switch to the blocked view is driven by the REAL server response: the
 * dialog always opens on the confirm view and only flips to blocked when
 * `onConfirm` resolves `in-use` (the API's 409). It never inspects the profile's
 * `assigned_users_count` to pre-decide the state.
 */
export function DeleteProfileDialog({
  profile,
  onClose,
  onDeleted,
  onConfirm,
}: DeleteProfileDialogProps) {
  const theme = useTheme();
  const { t } = useTranslation('rightsProfiles');
  const [submitting, setSubmitting] = useState(false);
  const [blocked, setBlocked] = useState<BlockedState | null>(null);

  // Reset to the confirm view whenever a different profile is opened.
  useEffect(() => {
    setBlocked(null);
  }, [profile]);

  const handleClose = () => {
    if (submitting) return;
    setBlocked(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (!profile) return;
    setSubmitting(true);
    try {
      const outcome = await onConfirm(profile);
      if (outcome.status === 'deleted') {
        onDeleted(profile);
        onClose();
      } else if (outcome.status === 'in-use') {
        // Blocked ONLY because the real API returned 409 — never pre-computed.
        setBlocked({
          profileName: outcome.profileName,
          assignedUsersCount: outcome.assignedUsersCount,
        });
      }
      // 'error': the hook already raised a toast; stay on the confirm view.
    } finally {
      setSubmitting(false);
    }
  };

  const pillSx = {
    borderRadius: theme.borderRadius.pill,
    textTransform: 'none' as const,
  };

  return (
    <AppModal
      open={profile !== null}
      onClose={handleClose}
      disableClose={submitting}
      maxWidth="xs"
      titleId="delete-profile-title"
      icon={
        blocked ? (
          <BlockOutlinedIcon sx={(th) => ({ fontSize: th.iconSize.sm })} />
        ) : (
          <DeleteOutlineIcon sx={(th) => ({ fontSize: th.iconSize.sm })} />
        )
      }
      title={blocked ? t('delete.blockedTitle') : t('delete.confirmTitle')}
      closeLabel={blocked ? t('delete.close') : t('delete.cancel')}
    >
      <DialogContent sx={{ pt: 2 }}>
        {blocked ? (
          <Alert severity="warning">
            {t('delete.blockedMessage', {
              name: blocked.profileName,
              userCount: blocked.assignedUsersCount,
            })}
          </Alert>
        ) : (
          <Typography>
            {t('delete.confirmMessage', { name: profile?.name ?? '' })}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {blocked ? (
          <Button variant="contained" onClick={handleClose} sx={pillSx}>
            {t('delete.close')}
          </Button>
        ) : (
          <>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={submitting}
              sx={pillSx}
            >
              {t('delete.cancel')}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirm}
              disabled={submitting}
              sx={pillSx}
            >
              {t('delete.confirm')}
            </Button>
          </>
        )}
      </DialogActions>
    </AppModal>
  );
}
